import React, { useEffect, useState } from "react";
import { ROLE_LABEL, type ImpersonationTarget } from "@analytics/shared";
import { api, useApp, type ViewId } from "../store.js";
import { Overview } from "../views/Overview.js";
import { LeadDistribution } from "../views/LeadDistribution.js";
import { Users } from "../views/Users.js";

const VIEWS: Record<ViewId, React.ComponentType> = {
  overview: Overview,
  leaddist: LeadDistribution,
  users: Users,
};

export function Shell() {
  const { session, view, setView, canSee } = useApp();
  const [menuOpen, setMenuOpen] = useState(false);

  const nav = ([
    { id: "overview", label: "Overview", icon: "ph ph-squares-four", show: true },
    { id: "leaddist", label: "Lead Distribution", icon: "ph ph-share-network", show: canSee("lead-distribution") },
    { id: "users", label: "Users", icon: "ph ph-users-three", show: Boolean(session?.canEditRoles) },
  ] as { id: ViewId; label: string; icon: string; show: boolean }[]).filter((n) => n.show);

  const ViewComp = VIEWS[view];

  return (
    <div style={{ display: "grid", gridTemplateColumns: "218px minmax(0, 1fr)", height: "100vh", background: "#161826", color: "#e9e9ed", fontFamily: "var(--font-body)", fontSize: "13px", overflow: "hidden" }}>
      {/* sidebar */}
      <aside style={{ display: "flex", flexDirection: "column", background: "#12141f", borderRight: "1px solid #292b31", minHeight: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: "9px", padding: "14px 14px 12px" }}>
          <div style={{ width: "26px", height: "26px", borderRadius: "7px", border: "1px solid #968ae0", display: "grid", placeItems: "center", color: "#b5abfc", fontSize: "14px" }}>
            <i className="ph ph-chart-donut" />
          </div>
          <div style={{ lineHeight: 1.1 }}>
            <div style={{ fontFamily: "var(--font-heading)", fontWeight: 500, fontSize: "14px", letterSpacing: "-0.01em" }}>Rever Analytics</div>
            <div style={{ fontSize: "10px", color: "#75798c", letterSpacing: "0.06em", textTransform: "uppercase" }}>dashboard portal</div>
          </div>
        </div>
        <nav style={{ display: "flex", flexDirection: "column", gap: "1px", padding: "6px 8px", overflowY: "auto", minHeight: 0 }}>
          {nav.map((n) => {
            const active = view === n.id;
            return (
              <button
                key={n.id}
                onClick={() => setView(n.id)}
                style={{
                  display: "flex", alignItems: "center", gap: "9px", padding: "7px 9px", borderRadius: "8px", cursor: "pointer",
                  font: "inherit", fontSize: "12.5px", textAlign: "left",
                  border: "1px solid " + (active ? "#423a6a" : "transparent"),
                  background: active ? "rgba(150,138,224,.13)" : "transparent",
                  color: active ? "#d2cefd" : "#b2b6ca",
                }}
              >
                <i className={n.icon} style={{ fontSize: "15px", width: "16px" }} />
                <span style={{ flex: 1, textAlign: "left" }}>{n.label}</span>
              </button>
            );
          })}
        </nav>
        <div style={{ marginTop: "auto", padding: "10px 12px 12px", borderTop: "1px solid #232532", fontSize: "10px", color: "#595d6c", lineHeight: 1.5 }}>
          More dashboards coming — access is managed in code.
          <div style={{ marginTop: "4px", color: "#454956", fontVariantNumeric: "tabular-nums" }}>
            {import.meta.env.VITE_APP_VERSION ?? "dev"}
          </div>
        </div>
      </aside>

      {/* main */}
      <main style={{ display: "flex", flexDirection: "column", minWidth: 0, minHeight: 0 }}>
        <header style={{ display: "flex", alignItems: "center", gap: "10px", padding: "0 14px", height: "50px", flex: "none", background: "#161826", borderBottom: "1px solid #292b31", position: "relative", zIndex: 30 }}>
          <div style={{ marginLeft: "auto", position: "relative" }}>
            <button onClick={() => setMenuOpen((v) => !v)} style={{ display: "flex", alignItems: "center", gap: "8px", padding: "3px 6px 3px 3px", background: "transparent", border: "1px solid transparent", borderRadius: "8px", color: "inherit", font: "inherit", cursor: "pointer" }}>
              <div style={{ width: "24px", height: "24px", borderRadius: "50%", background: "#423a6a", color: "#d2cefd", display: "grid", placeItems: "center", fontSize: "10px", fontWeight: 600 }}>
                {(session?.name ?? "?").split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase()}
              </div>
              <div style={{ lineHeight: 1.15, textAlign: "left" }}>
                <div style={{ fontSize: "11px" }}>{session?.email.split("@")[0]}</div>
                <div style={{ fontSize: "9px", letterSpacing: "0.06em", textTransform: "uppercase", color: "#968ae0" }}>
                  {session?.impersonating ? `as ${session.impersonating.name}` : (session ? ROLE_LABEL[session.role] : "")}
                </div>
              </div>
              <i className="ph ph-caret-down" style={{ fontSize: "11px", color: "#75798c" }} />
            </button>
            {menuOpen && <UserMenu onClose={() => setMenuOpen(false)} />}
          </div>
        </header>

        <div style={{ flex: 1, overflowY: "auto", minHeight: 0, padding: "16px 18px 40px" }}>
          <ViewComp />
        </div>
      </main>
    </div>
  );
}

function UserMenu({ onClose }: { onClose: () => void }) {
  const { session, setSession, reloadSession } = useApp();
  const [targets, setTargets] = useState<ImpersonationTarget[] | null>(null);
  const [busy, setBusy] = useState(false);
  const canImp = Boolean(session?.canImpersonate);
  const impersonating = session?.impersonating ?? null;

  useEffect(() => {
    if (!canImp) return;
    api.impersonateTargets().then(setTargets).catch(() => setTargets([]));
  }, [canImp]);

  // Reload the whole page after (un)impersonating so every data view + the nav re-fetch under the
  // new scope (changing the server-side scope alone doesn't change the client's useAsync deps).
  const start = async (email: string) => {
    if (busy) return;
    setBusy(true);
    try { await api.impersonate(email); window.location.reload(); }
    catch { alert("Không thể impersonate người dùng này."); setBusy(false); }
  };
  const stop = async () => {
    if (busy) return;
    setBusy(true);
    try { await api.stopImpersonate(); window.location.reload(); }
    catch { setBusy(false); }
  };
  const logout = async () => {
    try { await api.logout(); } catch { /* ignore */ }
    setSession(null);
    onClose();
  };
  void reloadSession;

  const item: React.CSSProperties = { display: "flex", alignItems: "center", gap: "8px", width: "100%", padding: "7px 8px", borderRadius: "8px", background: "transparent", border: 0, font: "inherit", cursor: "pointer", textAlign: "left" };

  return (
    <div style={{ position: "absolute", top: "40px", right: 0, width: "260px", zIndex: 60, background: "#232532", border: "1px solid #3f424d", borderRadius: "12px", boxShadow: "0 16px 40px rgba(0,0,0,.65)", padding: "6px", animation: "noc-fade .14s ease-out" }}>
      <div style={{ padding: "6px 8px 8px", fontSize: "11px", color: "#9397ab" }}>
        {session?.email}
        <div style={{ fontSize: "10px", color: "#75798c", marginTop: "2px" }}>{session ? ROLE_LABEL[session.role] : ""}</div>
      </div>
      <div style={{ height: "1px", background: "#292b31", margin: "2px 4px 6px" }} />

      {impersonating && (
        <button onClick={stop} disabled={busy} style={{ ...item, color: "#d2cefd" }}>
          <i className="ph ph-eye-slash" style={{ fontSize: "14px" }} /> Dừng xem với tư cách {impersonating.name}
        </button>
      )}

      {canImp && !impersonating && (
        <div style={{ padding: "2px 4px 6px" }}>
          <div style={{ fontSize: "9px", letterSpacing: "0.06em", textTransform: "uppercase", color: "#75798c", padding: "4px 6px" }}>Xem với tư cách</div>
          <div style={{ maxHeight: "240px", overflowY: "auto" }}>
            {targets == null && <div style={{ padding: "6px 8px", fontSize: "12px", color: "#75798c" }}>Đang tải…</div>}
            {targets?.length === 0 && <div style={{ padding: "6px 8px", fontSize: "12px", color: "#75798c" }}>Không có ai để xem.</div>}
            {targets?.map((t) => (
              <button key={t.email} onClick={() => start(t.email)} disabled={busy} style={{ ...item, color: "#cfd3e5", fontSize: "12px" }}>
                <i className="ph ph-user-switch" style={{ fontSize: "13px", color: "#968ae0" }} />
                <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{t.name}</span>
                <span style={{ fontSize: "10px", color: "#75798c" }}>{ROLE_LABEL[t.role]}{t.scoped ? ` · ${t.rvaCount} RVA` : ""}</span>
              </button>
            ))}
          </div>
          <div style={{ height: "1px", background: "#292b31", margin: "6px 4px 4px" }} />
        </div>
      )}

      <button onClick={logout} style={{ ...item, color: "#e79a9a" }}>
        <i className="ph ph-sign-out" style={{ fontSize: "14px" }} /> Sign out
      </button>
    </div>
  );
}
